import { FC } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { makeSxStyles } from "ui";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
} from "@/shared/components/header-items/HeaderItems";
import DashboardContentLayout from "@/shared/layouts/DashboardContentLayout";
import useRole from "@/hooks/use-role";
import { SettingsTabEnum } from "../constants/settings-tabs";
import ReplyEmailTab from "../components/ReplyEmailTab";
import ChangePasswordTab from "../components/ChangePasswordTab";

interface SettingsTab {
  value: SettingsTabEnum;
  label: string;
}

// Reached via the profile dropdown (navbar-menu-button.tsx). Org admins get
// two tabs (their org's reply email, plus password); super admins - who
// aren't bound to an organization - only get the password tab, but the tab
// strip itself still renders (with just that one tab) for a consistent
// screen structure across roles.
const SettingsPage: FC = () => {
  const styles = useStyles();
  const { isSuperAdmin } = useRole();
  const [params, setSearchParams] = useSearchParams();

  const tabs: SettingsTab[] = isSuperAdmin
    ? [{ value: SettingsTabEnum.password, label: "Password" }]
    : [
        { value: SettingsTabEnum.replyEmail, label: "Communication" },
        { value: SettingsTabEnum.password, label: "Password" },
      ];

  const requestedTab = params.get("tab") as SettingsTabEnum | null;
  const selectedTab =
    requestedTab && tabs.some((tab) => tab.value === requestedTab)
      ? requestedTab
      : tabs[0].value;

  return (
    <DashboardContentLayout horizontalPadding="40px">
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft breadcrumbs={[{ label: "Settings" }]} />
        </HeaderItems>
      </Navbar>
      <Box sx={styles.tabSection}>
        <Tabs
          value={selectedTab}
          onChange={(_e, value) =>
            setSearchParams((prev) => {
              prev.set("tab", value);
              return prev;
            })
          }
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              label={tab.label}
              value={tab.value}
              data-testid={`${tab.value}-tab`}
            />
          ))}
        </Tabs>
      </Box>
      <Box sx={styles.tabContent}>
        {selectedTab === SettingsTabEnum.replyEmail && <ReplyEmailTab />}
        {selectedTab === SettingsTabEnum.password && <ChangePasswordTab />}
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  tabSection: {
    mt: "24px",
    borderBottom: "1px solid #41474d",
  },
  tabContent: {
    mt: "24px",
  },
}));

export default SettingsPage;
